import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Box, Button, Group, MultiSelect, Select, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { ASSIGNABLE_ROLES, GEO_SCOPE_EXEMPT_ROLES, ROLE_LABELS } from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { DataTable, type Column } from '../components/DataTable';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { zodResolver } from '../lib/zod-resolver';
import classes from './EntityPage.module.css';

/**
 * Юзери, ролі та ГЕО-скоуп (§12) — екран, якого не існувало.
 *
 * Система тримала рівно один акаунт: super_admin, створений на старті процесу зі змінної
 * `SSO_ADMIN_EMAILS`. Другого не додати жодним запитом — тільки правкою `.env` і
 * перезапуском контейнера, тобто «CRM для команди» трималася на людині з доступом до сервера.
 *
 * Запрошення тут — **передавторизація пошти**, а не лист із паролем: увійти можна лише через
 * корпоративний брокер, і пароля в системі немає взагалі. Рядок каже брокеру, що цю пошту
 * чекають і з якою роллю.
 *
 * Матриця прав приходить із сервера, а не збирається тут: клієнт ніколи не вирішує доступ, він
 * лише показує відповідь. Копія матриці у вебі розійшлася б із тією, за якою реально
 * відмовляють, — і екран прав брехав би саме про права.
 */

interface UserRow {
  id: string;
  name: string;
  email: string;
  /** `null` — роль у базі, якої ця збірка не знає; акаунт відмовлено всюди. */
  role: string | null;
  geoScope: string[];
  geoCodes: string[];
  active: boolean;
  twoFactorEnabled: boolean;
  /** Пароль ще не встановлено — запрошення чекає. */
  passwordSet: boolean;
  isSelf: boolean;
}

const inviteSchema = z.object({
  email: z.string().trim().min(3, 'Вкажіть пошту').max(320).includes('@', { message: 'Це не пошта' }),
  role: z.enum(ASSIGNABLE_ROLES),
  geoScope: z.array(z.string()).optional(),
});
type InviteValues = z.infer<typeof inviteSchema>;

const LEVEL_LABELS: Record<string, string> = {
  full: 'повний',
  partial: 'частковий',
  read: 'читання',
  none: '—',
};

export function UsersPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('system_admin', 'write');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [scopeDraft, setScopeDraft] = useState<string[] | null>(null);

  const list = useQuery(trpc.users.list.queryOptions());
  const matrix = useQuery(trpc.users.matrix.queryOptions());
  const geos = useQuery(
    trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
  );

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.users.pathKey() });
  };

  const inviteForm = useForm<InviteValues>({
    initialValues: { email: '', role: 'seo', geoScope: [] as string[] },
    validate: zodResolver(inviteSchema),
  });

  const invite = useMutation(
    trpc.users.invite.mutationOptions({
      onSuccess: async () => {
        inviteForm.reset();
        setCreateOpen(false);
        await invalidate();
      },
    }),
  );

  const update = useMutation(
    trpc.users.update.mutationOptions({
      onSuccess: async () => {
        setScopeDraft(null);
        await invalidate();
      },
    }),
  );

  const resend = useMutation(
    trpc.users.resendInvite.mutationOptions({
      onSuccess: async () => {
        await invalidate();
      },
    }),
  );

  const rows = list.data ?? [];
  const editingRow = rows.find((row) => row.id === editing);

  const columns: Column<UserRow>[] = [
    {
      key: 'email',
      header: 'Пошта',
      render: (row) => (
        <Group gap={6}>
          <span>{row.email}</span>
          {row.isSelf ? (
            <Badge size="xs" variant="default" data-testid="user-self">
              це ви
            </Badge>
          ) : null}
        </Group>
      ),
    },
    { key: 'name', header: 'Імʼя', render: (row) => <span>{row.name}</span> },
    {
      key: 'role',
      header: 'Роль',
      render: (row) =>
        row.role === null ? (
          <Badge size="sm" color="red" data-testid={`user-role-${row.email}`}>
            роль не розпізнана
          </Badge>
        ) : (
          <Badge size="sm" variant="default" data-testid={`user-role-${row.email}`}>
            {ROLE_LABELS[row.role as keyof typeof ROLE_LABELS] ?? row.role}
          </Badge>
        ),
    },
    {
      key: 'scope',
      header: 'ГЕО-скоуп',
      /** Порожній скоуп у звуженої ролі — не «усі ринки», а «жодного». Різниця вирішальна. */
      render: (row) =>
        row.role === 'super_admin' ? (
          <Text className={classes.dim}>усі ринки</Text>
        ) : row.geoCodes.length === 0 ? (
          <Text className={classes.warn} data-testid={`user-scope-empty-${row.email}`}>
            жодного ринку
          </Text>
        ) : (
          <span className={classes.mono}>{row.geoCodes.join(', ')}</span>
        ),
    },
    {
      key: 'active',
      header: 'Стан',
      render: (row) => (
        <Badge
          size="sm"
          color={row.active ? 'mint' : 'slate'}
          data-testid={`user-active-${row.email}`}
        >
          {row.active ? 'активний' : 'вимкнений'}
        </Badge>
      ),
    },
    {
      key: 'password',
      header: 'Пароль',
      /** Запрошення чекає на встановлення пароля — рядок дає одразу й факт, і дію. */
      render: (row) =>
        row.passwordSet ? (
          <Text className={classes.dim}>встановлено</Text>
        ) : (
          <Group gap={6} wrap="nowrap">
            <Badge size="sm" color="amber" data-testid={`user-password-unset-${row.email}`}>
              пароль не встановлено
            </Badge>
            <Button
              size="compact-xs"
              variant="subtle"
              loading={resend.isPending}
              data-testid={`user-resend-${row.email}`}
              onClick={(event) => {
                event.stopPropagation();
                resend.mutate({ id: row.id });
              }}
            >
              Надіслати лист ще раз
            </Button>
          </Group>
        ),
    },
    {
      key: 'totp',
      header: '2FA',
      /** TOTP обовʼязковий лише для super_admin — для решти ролей клітинка порожня. */
      render: (row) =>
        row.role === 'super_admin' ? (
          <Badge size="sm" color={row.twoFactorEnabled ? 'mint' : 'red'} data-testid={`user-totp-${row.email}`}>
            {row.twoFactorEnabled ? 'увімкнено' : 'не увімкнено'}
          </Badge>
        ) : (
          <Text className={classes.dim}>—</Text>
        ),
    },
  ];

  return (
    <Stack gap="md">
      <CreatePanel
        onReset={() => {
          invite.reset();
          inviteForm.reset();
        }}
        zone="system_admin"
        label="Запросити"
        testId="user"
        open={createOpen}
        onOpenChange={setCreateOpen}
        busy={invite.isPending}
        error={invite.error}
        onSubmit={inviteForm.onSubmit((values) => {
          invite.mutate(values);
        })}
      >
        <TextInput
          size="xs"
          placeholder="пошта"
          data-testid="create-user-email"
          style={{ width: 220 }}
          {...inviteForm.getInputProps('email')}
        />
        <Select
          size="xs"
          allowDeselect={false}
          data={ASSIGNABLE_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
          data-testid="create-user-role"
          style={{ width: 170 }}
          {...inviteForm.getInputProps('role')}
        />
        {/* super_admin бачить усю мережу — скоуп для нього не питається, як і в редакторі рядка. */}
        {GEO_SCOPE_EXEMPT_ROLES.includes(inviteForm.values.role) ? null : (
          <MultiSelect
            size="xs"
            placeholder="ГЕО-скоуп"
            searchable
            clearable
            data={(geos.data?.items ?? []).map((geo) => ({
              value: geo.id,
              label: `${geo.countryCode} — ${geo.countryName}`,
            }))}
            data-testid="create-user-scope"
            style={{ width: 220 }}
            {...inviteForm.getInputProps('geoScope')}
          />
        )}
        <Text size="xs" className={classes.dim} style={{ flexBasis: '100%' }}>
          Особа отримає лист для встановлення пароля.
        </Text>
      </CreatePanel>

      <DataTable
        rows={rows}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Користувачів немає — але хтось же зараз дивиться на цей екран."
        onRowClick={(row) => {
          setEditing(row.id);
          setScopeDraft(null);
        }}
        rowTestId={(row) => `user-row-${row.email}`}
        testId="user-table"
      />

      {editingRow === undefined ? null : (
        <Box className={classes.panel} data-testid="user-editor">
          <Group justify="space-between" mb="sm">
            <Text className={classes.panelTitle}>{editingRow.email}</Text>
            <Button
              size="compact-xs"
              variant="subtle"
              data-testid="user-close"
              onClick={() => {
                setEditing(null);
              }}
            >
              Закрити
            </Button>
          </Group>

          {/*
           * Себе не можна ні понизити, ні вимкнути — і екран каже це прямо, а не гасить
           * контроли мовчки. Адміністратор, що зняв із себе роль, більше не має доступу до
           * цього екрана: повернути її нема кому й нема звідки, окрім правки бази.
           */}
          {editingRow.isSelf ? (
            <Text className={classes.warn} mb="sm" data-testid="user-self-note">
              Це ваш обліковий запис: власну роль і доступ змінити не можна.
            </Text>
          ) : null}

          {canWrite ? (
            <Group gap="sm" align="flex-end" wrap="wrap">
              <Select
                size="xs"
                label="Роль"
                allowDeselect={false}
                data={ASSIGNABLE_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
                value={editingRow.role}
                disabled={editingRow.isSelf}
                data-testid="user-role-select"
                style={{ width: 170 }}
                onChange={(value) => {
                  if (value === null) return;
                  update.mutate({
                    id: editingRow.id,
                    patch: { role: value, ...(value === 'super_admin' ? { geoScope: [] } : {}) },
                  });
                }}
              />
              <MultiSelect
                size="xs"
                label="ГЕО-скоуп"
                searchable
                clearable
                data={(geos.data?.items ?? []).map((geo) => ({
                  value: geo.id,
                  label: `${geo.countryCode} — ${geo.countryName}`,
                }))}
                value={scopeDraft ?? editingRow.geoScope}
                disabled={editingRow.role === 'super_admin'}
                data-testid="user-scope"
                style={{ width: 280 }}
                onChange={setScopeDraft}
              />
              <Button
                size="compact-xs"
                variant="default"
                loading={update.isPending}
                disabled={scopeDraft === null || editingRow.role === 'super_admin'}
                data-testid="user-scope-save"
                onClick={() => {
                  update.mutate({ id: editingRow.id, patch: { geoScope: scopeDraft ?? [] } });
                }}
              >
                Зберегти скоуп
              </Button>
              <Switch
                size="xs"
                label="активний"
                checked={editingRow.active}
                disabled={editingRow.isSelf}
                data-testid="user-active-toggle"
                onChange={(event) => {
                  update.mutate({
                    id: editingRow.id,
                    patch: { active: event.currentTarget.checked },
                  });
                }}
              />
            </Group>
          ) : null}

          {update.error === null ? null : (
            <Text className={classes.danger} mt={8} data-testid="user-error" role="alert">
              {publicMessageOf(update.error)}
            </Text>
          )}
        </Box>
      )}

      {/*
       * Матриця прав — не довідка, а відповідь на питання «що саме дає ця роль». Вона
       * приходить із сервера тим самим `PERMISSION_MATRIX`, за яким відмовляють процедури.
       */}
      {matrix.data === undefined ? null : (
        <Box className={classes.panel} data-testid="permission-matrix">
          <Text className={classes.panelTitle} mb={6}>
            Матриця прав
          </Text>
          <Stack gap={2}>
            {matrix.data.zones.map((zone) => (
              <Group key={zone.zone} gap={8} data-testid={`matrix-${zone.zone}`}>
                <Text size="sm" style={{ width: 220 }}>
                  {zone.label}
                </Text>
                {matrix.data.roles.map((role) => (
                  <Text
                    key={role}
                    className={zone.levels[role] === 'none' ? classes.dim : classes.mono}
                    style={{ width: 120 }}
                    data-testid={`matrix-${zone.zone}-${role}`}
                  >
                    {ROLE_LABELS[role]}: {LEVEL_LABELS[zone.levels[role] ?? 'none'] ?? zone.levels[role]}
                  </Text>
                ))}
              </Group>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
